import React, { useCallback, useState } from 'react';

import { Button } from '~/components/button';
import { ModalPicker } from '~/components/modal-picker';
import { FormattedMessage, useIntl } from '~/contexts/intl';
import { isWeb } from '~/utils';
import { CANCELLATION_TERMS_URL, openUrl, renderLink } from '~/utils/open-url';

import { AppointmentActionDescription, AppointmentActionTitle } from './appointment-actions';

export type AppointmentModifyProps = {
    visible: boolean;
    modifiable: boolean;
    onModifyCalendarEvent: () => Promise<void>;
    onCancel: () => void;
};

export function AppointmentModify(props: AppointmentModifyProps) {
    const { visible, modifiable, onModifyCalendarEvent, onCancel } = props;
    const [loading, setLoading] = useState<boolean>(false);
    const { formatMessage } = useIntl();

    const handleModifyCalendarEvent = useCallback(async () => {
        try {
            setLoading(true);
            await onModifyCalendarEvent();
        } finally {
            setLoading(false);
        }
    }, [onModifyCalendarEvent]);

    const handleLink = useCallback(async () => {
        // TODO: For now, we'll just open the cancellation terms URL for both cases
        // This can be updated later if different URLs are needed for different cases
        await openUrl(CANCELLATION_TERMS_URL);
    }, []);

    return (
        <ModalPicker
            visible={visible}
            bottomPadding={modifiable}
            extraStyles={isWeb() ? { width: 343, marginLeft: 'auto', marginRight: 'auto' } : undefined}
            renderContent={() => (
                <>
                    <AppointmentActionTitle>
                        <FormattedMessage id="appointment.modify.title" values={{ modifiable }} />
                    </AppointmentActionTitle>
                    <AppointmentActionDescription>
                        <FormattedMessage
                            id="appointment.modify.description"
                            values={{
                                link: renderLink(handleLink),
                                modifiable
                            }}
                        />
                    </AppointmentActionDescription>
                </>
            )}
            renderActions={() =>
                modifiable ? (
                    <Button
                        alignCenter
                        fillContainer
                        loading={loading}
                        type="secondary"
                        shape="rounded"
                        label={formatMessage('appointment.modify')}
                        onPress={handleModifyCalendarEvent}
                    />
                ) : null
            }
            onCancel={onCancel}
        />
    );
}
